'use client';
import IconCheck from '@mnd-frontend/icons/components/Check';
import {
  SPACING_2,
  WEB_PURPLE_LT,
  WEB_PURPLE_REG,
  WEB_RED_REG,
} from '@mnd-frontend/ui/shared/tokens';
import { type ChangeEventHandler, type ReactNode, useId, useState } from 'react';
import styled, { css } from 'styled-components';

interface CheckboxCommonInputProps {
  id?: string;
  disabled?: boolean;
  label?: ReactNode;
  'aria-label'?: string;
  variant?: Variant;
  name?: string;
  defaultValue?: boolean;
  error?: string | undefined;
}

export type CheckboxInputProps = CheckboxCommonInputProps &
  (
    | {
        checked: boolean;
        onChange: ChangeEventHandler<HTMLInputElement>;
      }
    | { checked?: undefined; onChange?: undefined }
  );

const CheckboxWrapper = styled.div`
  position: relative;
`;

export const checkboxVariants = ['on-light', 'on-dark'] as const;
type Variant = (typeof checkboxVariants)[number];

export const CheckboxVisual = styled.div<{ $variant: Variant; $hasError: boolean }>`
  height: 1.25rem;
  width: 1.25rem;
  ${({ $variant }) => {
    switch ($variant) {
      case 'on-light':
        return css`
          background-color: ${WEB_PURPLE_REG};
          border: 1px solid ${WEB_PURPLE_REG};
        `;
      case 'on-dark':
        return css`
          background-color: ${WEB_PURPLE_LT};
          border: 2px solid ${WEB_PURPLE_LT};
        `;
    }
  }}
  ${({ $hasError }) =>
    $hasError &&
    css`
    outline: 2px solid ${WEB_RED_REG};
  `}
  border-radius: 4px;
  transition: all 200ms ease;

  > * {
    display: block;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 1rem;
    height: 1rem;
    line-height: 0;
    svg {
      color: white;
    }
  }
`;
const CheckboxHidden = styled.input<{ $variant: Variant }>`
  position: absolute;
  z-index: 1;
  left: 0;
  opacity: 0;

  /*
    Hidden checkbox is only used as a hitarea so we want to make sure that it
    is always hidden and not effected by css resets overrides.
  */
  appearance: none !important;
  margin: 0 !important;
  padding: 0 !important;
  border: 0 !important;
  width: 100% !important;
  height: 100% !important;
  
  &[aria-checked="false"] {
    + ${CheckboxVisual} {
      background-color: transparent;
    }
  }

  &:focus-visible {
    outline: 2px solid ${WEB_PURPLE_LT};
    opacity: 1;
    border-radius: 0.25rem;
  }
`;

const Label = styled.label`
  display: flex;
  max-width: 100%;
  gap: ${SPACING_2};
  align-items: flex-start;
  user-select: none;
  cursor: pointer;
  ${CheckboxHidden} {
    cursor: pointer;
  }

  line-height: 0;
  font-size: 0.875rem;
  font-weight: 400;
`;

const LabelText = styled.span`
  line-height: 1.3125rem;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ErrorText = styled.div`
  color: ${WEB_RED_REG};
  font-size: 0.875rem;
  margin-top: 0.25rem;
`;

const Checkbox = ({
  label,
  disabled,
  onChange,
  checked: propsChecked,
  variant = 'on-light',
  defaultValue,
  error,
  ...props
}: CheckboxInputProps) => {
  const [checkedState, setCheckedState] = useState<boolean>(defaultValue ?? false);
  const checked = onChange ? propsChecked : checkedState;
  const ariaChecked = onChange ? propsChecked : checkedState;
  const id = useId();

  const showError = !!error && !checked;

  return (
    <div>
      <Label>
        <CheckboxWrapper>
          <CheckboxHidden
            {...props}
            type="checkbox"
            aria-checked={ariaChecked}
            aria-labelledby={id}
            aria-invalid={showError}
            aria-errormessage={`error-${props.name ?? id}`}
            checked={onChange ? checked : undefined}
            disabled={disabled}
            onChange={e => (onChange ? onChange(e) : setCheckedState(cs => !cs))}
            $variant={variant}
          />
          <CheckboxVisual $variant={variant} $hasError={showError} aria-hidden="true">
            <IconCheck style={{ opacity: checked ? 1 : 0 }} />
          </CheckboxVisual>
        </CheckboxWrapper>
        {label ? <LabelText id={id}>{label}</LabelText> : null}
      </Label>
      {showError && <ErrorText id={`error-${props.name ?? id}`}>{error}</ErrorText>}
    </div>
  );
};

export default Checkbox;
